import { useEffect, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { Pagination, PaginationParams } from '@/common/types';
import { API_URL } from '@/dtos';
import { EmployeeStatus } from '@/modules/employee/employee.constants';
import { Employee, StatusNames } from '@/modules/employee/employee.types';

export const QUERY_KEY = 'useEmployeesList';

export const getFullStatusName = (firstLetter: keyof typeof StatusNames) => StatusNames[firstLetter];

export interface EmployeeDtoPayload {
  id: string;
  first_name?: string;
  last_name?: string;
  status?: string;
  last_4_SSN_digits?: string;
  employer?: string;
  employer_employee_id?: string;
  partner_employee_id?: string;
  is_reimbursement_allowed?: boolean;
  employer_name?: string;
  subsidiary_name?: string;
  is_fraud_review_required?: boolean;
  fraud_review_required_at?: string;
}

export interface UseEmployeesListParams extends PaginationParams {
  observingOrganizationId?: string;
  searchEmployerId?: string;
  searchSubsidiaryId?: string;
  observingOrganizationType?: OrganizationTypes;
  options?: Partial<{
    enabled: boolean;
  }>;
}

const useEmployeesListQuery = ({
  page,
  perPage,
  searchString,
  status,
  options,
  searchEmployerId,
  searchSubsidiaryId,
  observingOrganizationId,
  observingOrganizationType,
}: UseEmployeesListParams) => {
  const { isLoading, data, refetch } = useQuery(
    [
      QUERY_KEY,
      page,
      perPage,
      status,
      searchString,
      observingOrganizationId,
      searchEmployerId,
      searchSubsidiaryId,
      observingOrganizationType,
    ],
    () =>
      api.get<Pagination<EmployeeDtoPayload>>(API_URL.EMPLOYEES_ADMIN_SEARCH, {
        params: {
          ...(status && status !== EmployeeStatus.Null ? { status: `${status}`[0].toUpperCase() } : {}),
          page,
          size: perPage,
          ...(searchString ? { search_value: searchString } : {}),
          ...(searchEmployerId || searchSubsidiaryId
            ? { organization_id: searchSubsidiaryId || searchEmployerId }
            : {}),
        },
      }),
    {
      enabled: false,
    }
  );
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    perPage,
    status,
    searchString,
    observingOrganizationId,
    searchEmployerId,
    searchSubsidiaryId,
    observingOrganizationType,
  ]);
  const formattedData = useMemo(
    () =>
      data?.data?.content?.map(
        (item): Employee => ({
          id: `${item.id}`,
          firstName: item.first_name || '',
          lastName: item.last_name || '',
          status: getFullStatusName(item.status as keyof typeof StatusNames),
          ssn: item.last_4_SSN_digits || '',
          last4Ssn: item.last_4_SSN_digits || '',
          employer: item.employer || '',
          employerEmployeeId: item.employer_employee_id || '',
          partnerEmployeeId: item.partner_employee_id || '',
          isReimbursementAllowed: item.is_reimbursement_allowed,
          employerName: item.employer_name || '',
          subsidiaryName: item.subsidiary_name || '',
          isFraudReviewRequired: Boolean(item.is_fraud_review_required),
          fraudReviewRequiredAt: item.fraud_review_required_at
            ? new Date(item.fraud_review_required_at).toISOString()
            : undefined,
        })
      ),
    [data]
  );
  const total = useMemo<number>(() => _.get(data, 'data.totalElements', 0) || 0, [data]);

  useEffect(() => {
    if (options?.enabled) refetch();
  }, [options, refetch]);

  return {
    data: formattedData || [],
    isLoading,
    total,
  };
};

export default useEmployeesListQuery;
